import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUPPORTED = [
  { code: 'USD', symbol: '$',  label: 'United States', flag: '🇺🇸' },
  { code: 'CAD', symbol: 'CA$', label: 'Canada',        flag: '🇨🇦' },
  { code: 'GBP', symbol: '£',  label: 'United Kingdom', flag: '🇬🇧' },
  { code: 'EUR', symbol: '€',  label: 'Eurozone',       flag: '🇪🇺' },
];
const TAX_INCLUSIVE = new Set(['GBP', 'EUR']);

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem('k_currency') || 'USD';
  });
  const [country, setCountry] = useState(() => localStorage.getItem('k_country') || '');
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoDetected, setAutoDetected] = useState(false);

  // Auto-detect on first ever load (no localStorage value)
  useEffect(() => {
    const stored = localStorage.getItem('k_currency');
    if (stored) {
      setAutoDetected(true);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/geo`);
        if (data?.currency) {
          setCurrency(data.currency);
          setCountry(data.country || '');
          localStorage.setItem('k_currency', data.currency);
          if (data.country) localStorage.setItem('k_country', data.country);
        }
      } catch (e) {
        // fallback to USD already set
      } finally {
        setAutoDetected(true);
      }
    })();
  }, []);

  // Fetch bundles whenever currency changes
  useEffect(() => {
    if (!autoDetected) return;
    setLoading(true);
    axios
      .get(`${API}/bundles`, { params: { currency } })
      .then((res) => setBundles(res.data || []))
      .catch(() => setBundles([]))
      .finally(() => setLoading(false));
  }, [currency, autoDetected]);

  const changeCurrency = (code) => {
    setCurrency(code);
    localStorage.setItem('k_currency', code);
  };

  const symbol = useMemo(
    () => SUPPORTED.find((c) => c.code === currency)?.symbol || '$',
    [currency]
  );
  const taxInclusive = TAX_INCLUSIVE.has(currency);

  const formatPrice = (amount) => {
    if (amount == null) return '';
    const fixed = Number(amount).toFixed(2);
    if (currency === 'EUR') return `€${fixed}`;
    if (currency === 'GBP') return `£${fixed}`;
    if (currency === 'CAD') return `CA$${fixed}`;
    return `$${fixed}`;
  };

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        symbol,
        country,
        taxInclusive,
        bundles,
        loading,
        changeCurrency,
        formatPrice,
        SUPPORTED,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
